import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { notificationService } from '../services/api';
import * as UI from '../styles/ui';
import EmptyState from './EmptyState';

interface Notification {
  notification_id: number;
  user_id: number;
  document_id: number;
  message: string;
  link?: string | null;
  status: 'unread' | 'read';
  created_at: string;
}

interface NotificationBellProps {
  variant?: 'dark' | 'light';
}

// How often the bell checks for new notifications. Short enough that a task
// someone just gave you pops up while you're still on the page.
const POLL_MS = 10000;
// How long a popup stays before it slides away on its own.
const TOAST_MS = 8000;
// At most this many popups at once; a burst (e.g. a batch of SLA reminders)
// shows the newest few and the rest wait in the bell.
const MAX_TOASTS = 3;

const NotificationBell: React.FC<NotificationBellProps> = ({ variant = 'dark' }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const [toasts, setToasts] = useState<Notification[]>([]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  // Ids already seen. The first load only fills this -- old notifications never
  // pop up when you sign in; only ones that arrive while you're here do.
  const seen = useRef<Set<number> | null>(null);

  const unreadCount = notifications.filter((n) => n.status === 'unread').length;

  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.notification_id !== id));
  }, []);

  const loadNotifications = useCallback(async () => {
    try {
      const res = await notificationService.getNotifications(0, 50);
      const list: Notification[] = res.data;
      setNotifications(list);
      if (seen.current === null) {
        seen.current = new Set(list.map((n) => n.notification_id));
        return;
      }
      const fresh = list.filter((n) => n.status === 'unread' && !seen.current!.has(n.notification_id));
      list.forEach((n) => seen.current!.add(n.notification_id));
      if (fresh.length) {
        setToasts((prev) => [...fresh, ...prev].slice(0, MAX_TOASTS));
        fresh.forEach((n) => window.setTimeout(() => dismissToast(n.notification_id), TOAST_MS));
      }
    } catch {
      // Silent: the bell simply tries again on the next tick.
    }
  }, [dismissToast]);

  useEffect(() => {
    loadNotifications();
    // Skip the network work while the tab is hidden; a visible tab is unchanged.
    // The focus listener below still fires an immediate load on return.
    const interval = setInterval(() => {
      if (document.hidden) return;
      loadNotifications();
    }, POLL_MS);
    // Coming back to the tab checks at once instead of waiting for the next tick.
    const onFocus = () => loadNotifications();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(interval); window.removeEventListener('focus', onFocus); };
  }, [loadNotifications]);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, []);

  useEffect(() => {
    if (open) {
      updatePosition();
    }
  }, [open, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openNotification = async (notif: Notification) => {
    dismissToast(notif.notification_id);
    if (notif.status === 'unread') {
      try {
        await notificationService.markNotificationAsRead(notif.notification_id);
        setNotifications((prev) =>
          prev.map((n) =>
            n.notification_id === notif.notification_id ? { ...n, status: 'read' } : n
          )
        );
      } catch {
        // Silent fail
      }
    }
    // AUDIT 4.3: this used to always go to /process/{document_id}, but
    // task/record/reminder notifications carry document_id=0, so they dead-ended
    // at /process/0. Route by the explicit link when present; else a real
    // document → the processor; else a safe module page instead of /process/0.
    if (notif.link) {
      navigate(notif.link);
    } else if (notif.document_id && notif.document_id > 0) {
      navigate(`/process/${notif.document_id}`);
    } else {
      navigate('/tasks');
    }
    setOpen(false);
  };

  const markAllRead = async () => {
    try {
      await notificationService.markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'read' })));
    } catch {
      // Silent fail
    }
  };

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now.getTime() - d.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          className="fixed w-80 max-h-96 bg-white dark:bg-slate-800 rounded-[20px] shadow-2xl border border-gray-200 dark:border-slate-700 overflow-hidden"
          style={{ top: dropdownPos.top, right: dropdownPos.right, zIndex: 9999, fontFamily: UI.FONT }}
        >
          <div className="px-4 py-3 border-b border-gray-200 dark:border-slate-700 flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Notifications {unreadCount > 0 && <span style={{ color: UI.COLORS.red }}>({unreadCount})</span>}
            </h3>
            {unreadCount > 0 && (
              <button type="button" onClick={markAllRead}
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: UI.FONT,
                         fontSize: 12, fontWeight: 600, color: UI.COLORS.navy }}>
                Mark all read
              </button>
            )}
          </div>

          <div className="overflow-y-auto max-h-80">
            {notifications.length === 0 ? (
              <EmptyState flat text="No notifications" />
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  onClick={() => openNotification(notif)}
                  className={`px-4 py-3 border-b border-gray-100 dark:border-slate-700 cursor-pointer transition-colors ${
                    notif.status === 'unread'
                      ? 'bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30'
                      : 'hover:bg-gray-50 dark:hover:bg-slate-700/50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    {notif.status === 'unread' && (
                      <span className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0" style={{ background: UI.COLORS.navy }} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-xs leading-relaxed ${
                          notif.status === 'unread'
                            ? 'text-gray-900 dark:text-white font-medium'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {notif.message}
                      </p>
                      <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                        {formatTime(notif.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  // Live popups, bottom-right: a new notification shows here the moment the bell
  // picks it up, and stays in the bell afterwards. Click opens it; × dismisses.
  const toastStack = toasts.length
    ? createPortal(
        <div role="status" aria-live="polite"
          style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 10000, display: 'flex',
                   flexDirection: 'column', gap: 10, width: 340, maxWidth: 'calc(100vw - 40px)', fontFamily: UI.FONT }}>
          {toasts.map((t) => (
            <div key={t.notification_id} onClick={() => openNotification(t)}
              style={{ ...UI.card, marginBottom: 0, padding: '12px 14px', cursor: 'pointer',
                       borderLeft: `4px solid ${UI.COLORS.navy}`, boxShadow: '0 12px 30px rgba(15,23,42,0.18)',
                       display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ color: UI.COLORS.navy, flexShrink: 0, marginTop: 1 }}>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary-text)', textTransform: 'uppercase',
                              letterSpacing: '0.05em', marginBottom: 2 }}>New notification</div>
                <div style={{ fontSize: 13, color: 'var(--color-gray-900)', lineHeight: 1.45, wordBreak: 'break-word' }}>{t.message}</div>
              </div>
              <button type="button" aria-label="Dismiss"
                onClick={(e) => { e.stopPropagation(); dismissToast(t.notification_id); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)',
                         fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
            </div>
          ))}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => setOpen(!open)}
        className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200 flex items-center relative ${
          variant === 'dark'
            ? 'bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm border border-white/30'
            : 'bg-gray-100 hover:bg-gray-200 dark:bg-slate-700 dark:hover:bg-slate-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-slate-600'
        }`}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
          />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {dropdown}
      {toastStack}
    </>
  );
};

export default NotificationBell;
